import * as Dialog from '@radix-ui/react-dialog'
import { ArrowRight, X } from 'lucide-react'

import { CoverageInsightsPanel } from './CoverageInsightsPanel'
import { CoverageKpiTiles, IssueKpiTiles } from './DashboardKpis'
import { IssuesInsightsPanel } from './IssuesInsightsPanel'
import { useBoardShortcuts } from '../../../hooks/useBoardShortcuts'
import { useDashboardInsights } from '../../../hooks/useDashboardInsights'
import { prettyName } from '../../../lib/mandal-name'
import { periodLabel } from '../../../lib/period-label'

import type { DashboardBoard } from './DashboardButton'
import type { GisOverview, GisScope, GisTab } from '../../../api/types'

const TITLES: Record<DashboardBoard, string> = {
  issues: 'Issues dashboard',
  coverage: 'Coverage dashboard',
}

/** "District-wide" or the pretty-named mandal scope (truncated to the first two + a count). */
function scopeText(activeMandals: readonly string[]): string {
  if (activeMandals.length === 0) {
    return 'District-wide'
  }
  const names = activeMandals.slice(0, 2).map(prettyName).join(', ')
  const rest = activeMandals.length - 2
  return rest > 0 ? `${names} +${rest} more` : names
}

interface DashboardOverlayProps {
  board: DashboardBoard
  overview: GisOverview
  scope: GisScope
  activeMandals: readonly string[]
  onClose: () => void
  /** Navigate to a board tab (the overlay closes itself first, then jumps). */
  onOpenLens: (tab: GisTab) => void
  onSelectMandal: (name: string) => void
  onSelectIssue: (id: string, siteId?: string | null) => void
}

/**
 * One board's full dashboard as a WINDOW on top of the page (opened from the tab bar's Dashboards
 * dropdown): that board's KPI tiles + its complete insights panel — the exact modules the Dashboard
 * tab renders, from the same `useDashboardInsights` view-model, so window and tab can never
 * disagree. Every drill closes the window first, then lands on the filtered board / issue detail.
 * Needs-attention mandal rows re-scope IN PLACE (the window stays up and re-cuts live).
 */
export function DashboardOverlay({ board, overview, scope, activeMandals, onClose, onOpenLens, onSelectMandal, onSelectIssue }: DashboardOverlayProps) {
  const i = useDashboardInsights(overview, scope, activeMandals)
  const go = useBoardShortcuts({
    onOpenLens: (tab) => {
      onClose()
      onOpenLens(tab)
    },
    onSelectMandal,
    activeMandals,
  })
  const openIssue = (id: string, siteId?: string | null): void => {
    onClose()
    onSelectIssue(id, siteId)
  }

  // Radix Dialog in a PORTAL (the gallery modal's exact pattern): the window mounts on <body> at
  // z-[9999], above the app's fixed nav shell (z-40) — the whole chrome dims behind the backdrop.
  return (
    <Dialog.Root open onOpenChange={(o) => { if (!o) { onClose() } }}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-[9998] bg-[#0b1220]/55 backdrop-blur-[2px] data-[state=open]:animate-in data-[state=open]:fade-in" />
        <Dialog.Content
          aria-describedby={undefined}
          className="fixed top-1/2 left-1/2 z-[9999] flex max-h-[min(90vh,880px)] w-[min(96vw,1120px)] -translate-x-1/2 -translate-y-1/2 flex-col overflow-hidden rounded-[16px] bg-gis-panel shadow-gis-pop focus:outline-none data-[state=open]:animate-in data-[state=open]:fade-in data-[state=open]:zoom-in-95"
        >
          <header className="flex flex-none items-center justify-between gap-3 border-b border-gis-line bg-white px-4 py-[10px]">
            <div className="min-w-0">
              <Dialog.Title asChild>
                <h2 className="text-[14px] leading-tight font-bold text-gis-ink">{TITLES[board]}</h2>
              </Dialog.Title>
              <p className="truncate text-[10.5px] text-gis-soft">
                {periodLabel(scope.period, scope.dateFrom, scope.dateTo)} · {scopeText(activeMandals)} · every module drills into the board
              </p>
            </div>
            <div className="flex flex-none items-center gap-2">
              {/* The window's one primary action — land on the board itself (unfiltered). */}
              <button
                type="button"
                onClick={() => (board === 'issues' ? go.goIssues() : go.goCoverage())}
                className="flex items-center gap-1 rounded-[9px] border border-gis-line bg-white px-[9px] py-[5px] text-[11px] font-semibold text-gis-heading transition-colors hover:border-[#c7d2ea] hover:bg-[#fbfcfe] hover:text-gis-heading-hover focus-visible:ring-2 focus-visible:ring-blue-500/60"
              >
                Open board
                <ArrowRight className="h-[12px] w-[12px]" aria-hidden />
              </button>
              <Dialog.Close asChild>
                <button
                  type="button"
                  aria-label="Close"
                  className="grid h-[28px] w-[28px] flex-none place-items-center rounded-[8px] text-gis-soft transition-colors hover:bg-gis-track hover:text-gis-ink focus-visible:ring-2 focus-visible:ring-blue-500/60"
                >
                  <X className="h-[15px] w-[15px]" />
                </button>
              </Dialog.Close>
            </div>
          </header>
          <div className="@container/dash min-h-0 flex-1 overflow-y-auto p-3.5">
            <div className="flex flex-col gap-3">
              <div className="grid grid-cols-2 gap-2 @[560px]/dash:grid-cols-4">
                {board === 'issues' ? <IssueKpiTiles i={i} go={go} /> : <CoverageKpiTiles i={i} go={go} />}
              </div>
              {board === 'issues' ? (
                <IssuesInsightsPanel i={i} go={go} onSelectIssue={openIssue} onSelectMandal={onSelectMandal} />
              ) : (
                <CoverageInsightsPanel i={i} go={go} />
              )}
            </div>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}
